import { createFileRoute } from '@tanstack/react-router'
import { Plus, FolderTree, TrendingUp, TrendingDown, ChevronRight, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { CategoryForm } from '@/components/finance/category/category-form'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import {
  useCategories,
  useDeleteCategory,
  type CategoryWithChildren,
  type CategoryType,
} from '@/hooks'
import { useConfirmDialog } from '@/hooks'

export const Route = createFileRoute('/_authenticated/finance/categories/' as never)({
  component: CategoryListPage,
})

function CategoryListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CategoryWithChildren | null>(null)
  const [createType, setCreateType] = useState<CategoryType>('expense')
  const [createParentId, setCreateParentId] = useState<string | undefined>()

  const { data: categories, isLoading, error } = useCategories()
  const deleteMutation = useDeleteCategory()
  const toast = useToast()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDelete = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除此分类吗？此操作不可恢复。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : '删除失败')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleCreateChild = (parentId: string, type: CategoryType) => {
    setCreateParentId(parentId)
    setCreateType(type)
    setShowCreateForm(true)
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const incomeCategories = categories?.income ?? []
  const expenseCategories = categories?.expense ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">分类管理</h1>
          <p className="text-muted-foreground">管理收支分类</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setCreateType('income')
              setCreateParentId(undefined)
              setShowCreateForm(true)
            }}
            className="border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            新建收入分类
          </Button>
          <Button
            onClick={() => {
              setCreateType('expense')
              setCreateParentId(undefined)
              setShowCreateForm(true)
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            新建支出分类
          </Button>
        </div>
      </div>

      {(showCreateForm || editingCategory) && (
        <CategoryForm
          category={editingCategory ?? undefined}
          defaultType={createType}
          parentId={createParentId}
          onClose={() => {
            setShowCreateForm(false)
            setEditingCategory(null)
            setCreateParentId(undefined)
          }}
          onSuccess={() => {
            setShowCreateForm(false)
            setEditingCategory(null)
            setCreateParentId(undefined)
          }}
        />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : incomeCategories.length === 0 && expenseCategories.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <FolderTree className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无分类</p>
            <p className="text-sm text-muted-foreground">点击上方按钮创建分类</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <CategorySection
            title="收入分类"
            icon={TrendingUp}
            iconColor="text-green-600"
            categories={incomeCategories}
            type="income"
            onEdit={setEditingCategory}
            onDelete={(id) => void handleDelete(id)}
            onCreateChild={handleCreateChild}
          />
          <CategorySection
            title="支出分类"
            icon={TrendingDown}
            iconColor="text-red-600"
            categories={expenseCategories}
            type="expense"
            onEdit={setEditingCategory}
            onDelete={(id) => void handleDelete(id)}
            onCreateChild={handleCreateChild}
          />
        </div>
      )}

      <ConfirmDialog />
    </div>
  )
}

interface CategorySectionProps {
  title: string
  icon: typeof TrendingUp
  iconColor: string
  categories: CategoryWithChildren[]
  type: CategoryType
  onEdit: (category: CategoryWithChildren) => void
  onDelete: (id: string) => void
  onCreateChild: (parentId: string, type: CategoryType) => void
}

function CategorySection({
  title,
  icon: Icon,
  iconColor,
  categories,
  type,
  onEdit,
  onDelete,
  onCreateChild,
}: CategorySectionProps) {
  return (
    <div className="rounded-lg border">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
        <Icon className={`h-5 w-5 ${iconColor}`} />
        <h2 className="font-semibold">{title}</h2>
        <span className="text-sm text-muted-foreground">({categories.length})</span>
      </div>
      <div className="p-2">
        {categories.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">暂无分类</p>
        ) : (
          <div className="space-y-1">
            {categories.map((category) => (
              <CategoryItem
                key={category.id}
                category={category}
                type={type}
                level={0}
                onEdit={onEdit}
                onDelete={onDelete}
                onCreateChild={onCreateChild}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface CategoryItemProps {
  category: CategoryWithChildren
  type: CategoryType
  level: number
  onEdit: (category: CategoryWithChildren) => void
  onDelete: (id: string) => void
  onCreateChild: (parentId: string, type: CategoryType) => void
}

function CategoryItem({
  category,
  type,
  level,
  onEdit,
  onDelete,
  onCreateChild,
}: CategoryItemProps) {
  const [expanded, setExpanded] = useState(true)
  const hasChildren = category.children && category.children.length > 0

  return (
    <div>
      <div
        className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        {hasChildren ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setExpanded(!expanded)}
            className="h-6 w-6 p-0.5"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        ) : (
          <span className="w-5" />
        )}
        <span className="flex-1">{category.name}</span>
        <div className="hidden gap-1 group-hover:flex">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCreateChild(category.id, type)}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
          >
            添加子分类
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(category)}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
          >
            编辑
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(category.id)}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
          >
            删除
          </Button>
        </div>
      </div>
      {hasChildren && expanded && (
        <div>
          {(category.children ?? []).map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              type={type}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onCreateChild={onCreateChild}
            />
          ))}
        </div>
      )}
    </div>
  )
}
